import { apiSlice } from './apiSlice';
import { Field, adaptField } from '../models/Field';
import { Plot, adaptPlot } from '../models/Plot';
import { Bed, adaptBed } from '../models/Bed';
import { Action } from '../models/Farm';
import { loadSaveWorldSnapshot, toQueryFnError } from './saveWorldQuery';

export interface BedProperty {
  propertyId: number;
  propertyCode: string;
  propertyName: string;
  dataType: string;
  semanticType: string;
  unit: string | null;
  valueText: string | null;
  valueNumber: number | null;
  valueBoolean: boolean | null;
  valueDate: string | null;
  updatedAt: string;
}

export interface RotationCheckReason {
  code: string;
  label: string;
  message: string;
  factor: number;
}

export interface RotationCheckResult {
  bedId: string;
  cropId: string;
  family: string;
  familyRu: string;
  severity: 'none' | 'mild' | 'moderate' | 'severe';
  penaltyPct: number;
  rotationFactor: number;
  reasons: RotationCheckReason[];
  state: {
    sameFamilyExposureDays: number;
    pathogenPressure: number;
    soilRecoveryIndex: number;
  };
  recommendation: string | null;
  recoveryYears: number;
  fallowDays: number;
}

export interface RotationHistoryEntry {
  plantingId: string;
  cropName: string;
  familyLatin: string;
  familyRu: string;
  familyColor: string;
  fromDate: string | null;
  toDate: string | null;
  durationDays: number | null;
  isActive: boolean;
  /** True when an entry is shown visually but excluded from rotation totals */
  isExcluded?: boolean;
  lifecycleStatus?: string;
}

export interface RotationHistoryFamily {
  familyLatin: string;
  familyRu: string;
  color: string;
  totalDays: number;
  count: number;
}

export interface RotationHistoryResult {
  bedId: string;
  entries: RotationHistoryEntry[];
  families: RotationHistoryFamily[];
  totalEntries: number;
  simDate?: string;
}

export const fieldsApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getFieldById: build.query<Field, { saveId: string; fieldId: string }>({
      queryFn: async ({ saveId, fieldId }, api) => {
        try {
          const world = await loadSaveWorldSnapshot(api, saveId);
          const field = world.structure.fields.find((item) => String(item.id) === String(fieldId));
          const plots = world.structure.plots.filter((plot) => String(plot.fieldId) === String(fieldId));
          return {
            data: adaptField({
              id: field?.id ?? fieldId,
              name: field?.name ?? null,
              farmId: field?.farmId ?? world.farm.id,
              plots,
              actions: [],
            }),
          };
        } catch (error) {
          return { error: toQueryFnError(error) };
        }
      },
      providesTags: (result, error, { saveId, fieldId }) => [
        { type: 'Field' as const, id: fieldId },
        { type: 'SaveV2' as const, id: `world-${saveId}` },
      ],
    }),
    listFields: build.query<Field[], string | number>({
      query: (farmId) => ({
        url: `/field`,
        params: { farmId },
      }),
      transformResponse: (response: any) => {
        const items = response?.fields ?? response ?? [];
        return Array.isArray(items) ? items.map((field: any) => adaptField(field)) : [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((item) => ({ type: 'Field' as const, id: String(item.id) })),
              { type: 'Field' as const, id: 'LIST' },
            ]
          : [{ type: 'Field' as const, id: 'LIST' }],
    }),
    getPlotById: build.query<{ plot: Plot; field?: Field; actions: Action[] }, { saveId: string; plotId: string; fieldId?: string }>({
      queryFn: async ({ saveId, plotId }, api) => {
        try {
          const world = await loadSaveWorldSnapshot(api, saveId);
          const plotSource = world.structure.plots.find((item) => String(item.id) === String(plotId));
          const beds = world.structure.beds.filter((bed) => String(bed.plotId) === String(plotId));
          const fieldSource = plotSource?.fieldId
            ? world.structure.fields.find((field) => String(field.id) === String(plotSource.fieldId))
            : undefined;
          const plot = adaptPlot({
            ...(plotSource || { id: plotId, name: null }),
            beds,
            actions: [],
          });
          const field = fieldSource ? adaptField(fieldSource) : undefined;
          const actions = plot.actions ?? [];
          return { data: { plot, field, actions } };
        } catch (error) {
          return { error: toQueryFnError(error) };
        }
      },
      providesTags: (result, error, { saveId, plotId }) => [
        { type: 'Plot' as const, id: plotId },
        { type: 'SaveV2' as const, id: `world-${saveId}` },
      ],
    }),
    listPlots: build.query<Plot[], string | number>({
      query: (fieldId) => ({
        url: `/plot`,
        params: { fieldId },
      }),
      transformResponse: (response: any) => {
        const items = response?.plots ?? response ?? [];
        return Array.isArray(items) ? items.map((plot: any) => adaptPlot(plot)) : [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((item) => ({ type: 'Plot' as const, id: String(item.id) })),
              { type: 'Plot' as const, id: 'LIST' },
            ]
          : [{ type: 'Plot' as const, id: 'LIST' }],
    }),
    getBedById: build.query<Bed, { bedId: string; plotId?: string }>({
      query: ({ bedId, plotId }) => ({ 
        url: `/bed/${bedId}`,
        params: plotId ? { plotId } : undefined,
      }),
      transformResponse: (response: any) => adaptBed(response?.bed ?? response),
      providesTags: (result, error, { bedId }) => [{ type: 'Bed' as const, id: bedId }],
    }),
    listBedsByPlot: build.query<Bed[], string | number>({
      query: (plotId) => ({
        url: `/bed`,
        params: { plotId },
      }),
      transformResponse: (response: any) => {
        const items = response?.beds ?? response ?? [];
        return Array.isArray(items) ? items.map((bed: any) => adaptBed(bed)) : [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((item) => ({ type: 'Bed' as const, id: String(item.id) })),
              { type: 'Bed' as const, id: 'LIST' },
            ]
          : [{ type: 'Bed' as const, id: 'LIST' }],
    }),
    updateField: build.mutation< Field, { saveId: string; id: string; payload: Partial<Pick<Field, 'name' | 'area' | 'location_coords' | 'description'>> } >({
      query: ({ saveId, id, payload }) => ({ url: `/saves/${saveId}/fields/${id}`, method: 'PATCH', body: payload }),
      transformResponse: (response: any) => {
        const { field, plots, sections } = response || {};
        if (field) return adaptField({ ...field, plots: plots ?? sections });
        return adaptField(response);
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Field' as const, id }],
    }),
    deleteField: build.mutation<void, { saveId: string; id: string }>({
      query: ({ saveId, id }) => ({ url: `/saves/${saveId}/fields/${id}`, method: 'DELETE' }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Field' as const, id }],
    }),
    createPlot: build.mutation<
      Plot,
      { saveId: string; fieldId: string; name: string; area?: number; location_coords?: string; description?: string }
    >({
      query: ({ saveId, fieldId, name, area, location_coords, description }) => ({
        url: `/saves/${saveId}/fields/${fieldId}/plots`,
        method: 'POST',
        body: { name, area, location_coords, description },
      }),
      transformResponse: (response: any) => adaptPlot(response?.plot || response),
      invalidatesTags: (result, error, { fieldId }) => [{ type: 'Field' as const, id: fieldId }],
    }),
    updatePlot: build.mutation<
      Plot,
      {
        saveId: string;
        id: string;
        payload: Partial<Pick<Plot, 'name' | 'area' | 'location_coords' | 'description'>>;
      }
    >({
      query: ({ saveId, id, payload }) => ({ url: `/saves/${saveId}/plots/${id}`, method: 'PATCH', body: payload }),
      transformResponse: (response: any) => adaptPlot(response?.plot || response),
      invalidatesTags: (result, error, { id }) => [{ type: 'Plot' as const, id }],
    }),
    deletePlot: build.mutation<void, { saveId: string; plotId: string; fieldId?: string }>({
      query: ({ saveId, plotId }) => ({ url: `/saves/${saveId}/plots/${plotId}`, method: 'DELETE' }),
      invalidatesTags: (result, error, { plotId, fieldId }) => [
        { type: 'Plot' as const, id: plotId },
        ...(fieldId ? [{ type: 'Field' as const, id: fieldId }] : []),
      ],
    }),
    createBed: build.mutation<
      Bed,
      {
        plotId: string;
        area?: number;
        size_cm_encoded?: number;
        geo_location?: string;
        row_number?: number;
        position_number?: number;
      }
    >({
      query: ({ plotId, ...payload }) => ({
        url: `/bed`,
        method: 'POST',
        body: { plotId, ...payload },
      }),
      transformResponse: (response: any) => adaptBed(response?.bed || response),
      invalidatesTags: (result, error, { plotId }) => [{ type: 'Plot' as const, id: plotId }],
    }),
    updateBed: build.mutation<
      Bed,
      {
        id: string;
        payload: Partial<Pick<Bed, 'name' | 'area' | 'size_cm_encoded' | 'geo_location'>>;
      }
    >({
      query: ({ id, payload }) => ({ url: `/bed/${id}`, method: 'PATCH', body: payload }),
      transformResponse: (response: any) => adaptBed(response?.bed || response),
      invalidatesTags: (result, error, { id }) => [{ type: 'Bed' as const, id }],
    }),
    getBedRotationCheck: build.query<RotationCheckResult, { bedId: string; cropId: string }>({
      query: ({ bedId, cropId }) => ({
        url: `/bed/${bedId}/rotation-check`,
        params: { cropId },
      }),
      providesTags: (result, error, { bedId }) => [{ type: 'Bed' as const, id: `${bedId}-rotation` }],
    }),
    getBedRotationHistory: build.query<RotationHistoryResult, { bedId: string; simSessionId?: string | null }>({
      query: ({ bedId, simSessionId }) => ({
        url: `/bed/${bedId}/rotation-history`,
        params: simSessionId ? { simSessionId } : undefined,
      }),
      providesTags: (result, error, { bedId }) => [{ type: 'Bed' as const, id: `${bedId}-rotation` }],
    }),
    getBedProperties: build.query<BedProperty[], { bedId: string; simSessionId?: string | null }>({
      query: ({ bedId, simSessionId }) => ({
        url: `/bed/${bedId}/properties`,
        params: simSessionId ? { simSessionId } : undefined,
      }),
      transformResponse: (response: any) => {
        const rows = response?.properties ?? response ?? [];
        return Array.isArray(rows) ? rows.map((r: any) => ({
          propertyId: r.property_id,
          propertyCode: r.property_code,
          propertyName: r.property_name,
          dataType: r.data_type,
          semanticType: r.semantic_type,
          unit: r.unit ?? null,
          valueText: r.value_text ?? null,
          valueNumber: r.value_number ?? null,
          valueBoolean: r.value_boolean ?? null,
          valueDate: r.value_date ?? null,
          updatedAt: r.updated_at,
        })) : [];
      },
      providesTags: (result, error, { bedId }) => [{ type: 'Bed' as const, id: `${bedId}-props` }],
    }),
  }),
});

export const {
  useGetFieldByIdQuery,
  useListFieldsQuery,
  useLazyListFieldsQuery,
  useGetPlotByIdQuery,
  useListPlotsQuery,
  useLazyListPlotsQuery,
  useGetBedByIdQuery,
  useListBedsByPlotQuery,
  useLazyListBedsByPlotQuery,
  useUpdateFieldMutation,
  useDeleteFieldMutation,
  useCreatePlotMutation,
  useUpdatePlotMutation,
  useDeletePlotMutation,
  useCreateBedMutation,
  useUpdateBedMutation,
  useGetBedRotationCheckQuery,
  useLazyGetBedRotationCheckQuery,
  useGetBedRotationHistoryQuery,
  useGetBedPropertiesQuery,
} = fieldsApi;
